import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import configApi from '~/apis/config';
import seniorApi, { ISeniorProfileResponse } from '~/apis/senior';
import { NumberToggleRow, SegmentedBoxes, SettingsCard, SwitchRow } from '~/components/module/setup/controls';
import { CONFIG_DEFAULT } from '~/utils/constants/configDefault';
import {
  MAX_DATA_RETENTION_DAYS,
  MAX_FALL_DETECT_SENSITIVITY_LEVEL,
  SECONDS_PER_MINUTE,
  SENIOR_SETTINGS_DEFAULT as DEFAULT,
} from '~/utils/constants/seniorSettings';
import { getErrorMessage } from '~/utils/helper/error';
import { canViewModule } from '~/utils/helper/permission';
import { toMultipart } from '~/utils/helper/setupForm';
import { IConfigValues, IUpdateConfigItem } from '~/utils/interface/config';
import { useAppSelector } from '~/utils/redux/hooks';
import { selectPermission, selectUser } from '~/thunks/auth/authSlice';
import { setupRouteAbsolute } from '~/utils/routes/setup';

// Senior Settings: saved with PUT /seniors/profile, like the app (minutes here, seconds on the wire).
interface ISettings {
  blockUnknownNumber: boolean;
  volume: number;
  brightness: number;
  themeMode: string;
  flashlightOn: boolean;
  flashlightTimeout: number;
  screenTimeout: number;
  autoAnswerDelaySeconds: number;
  fallDetectSensitivity: number;
  inactivityTimeout: number;
  thresholdLowBattery: number;
  dataRetentionDays: number;
}

// Alert Notification Settings (who gets which alert): saved with PUT /configs/multiple.
interface IExtras {
  alertRecipients: Record<string, boolean>;
}

type IDraft = ISettings & IExtras;

const minutes = (seconds: number | undefined, fallback: number) =>
  seconds != null ? seconds / SECONDS_PER_MINUTE : fallback;

// Saved values win; anything never saved falls back to the app's default.
const toDraft = (data: ISeniorProfileResponse, config: IConfigValues): IDraft => ({
  blockUnknownNumber: data.blockUnknownNumber ?? DEFAULT.blockUnknownNumber,
  volume: data.volume ?? DEFAULT.volume,
  brightness: data.brightness ?? DEFAULT.brightness,
  themeMode: data.themeMode ?? DEFAULT.themeMode,
  flashlightOn: data.flashlightOn ?? DEFAULT.flashlightOn,
  flashlightTimeout: minutes(data.flashlightTimeout, DEFAULT.flashlightTimeoutMinutes),
  screenTimeout: minutes(data.screenTimeout, DEFAULT.screenTimeoutMinutes),
  autoAnswerDelaySeconds: data.autoAnswerDelaySeconds ?? DEFAULT.autoAnswerDelaySeconds,
  fallDetectSensitivity: data.fallDetectSensitivity ?? DEFAULT.fallDetectSensitivity,
  inactivityTimeout: minutes(data.inactivityTimeout, DEFAULT.inactivityTimeoutMinutes),
  thresholdLowBattery: data.thresholdLowBattery ?? DEFAULT.thresholdLowBattery,
  dataRetentionDays: data.dataRetentionDays ?? DEFAULT.dataRetentionDays,
  alertRecipients: { ...CONFIG_DEFAULT.alertRecipients, ...config.alertRecipients },
});

const SETTING_KEYS: (keyof ISettings)[] = [
  'blockUnknownNumber',
  'volume',
  'brightness',
  'themeMode',
  'flashlightOn',
  'flashlightTimeout',
  'screenTimeout',
  'autoAnswerDelaySeconds',
  'fallDetectSensitivity',
  'inactivityTimeout',
  'thresholdLowBattery',
  'dataRetentionDays',
];

// These three are minutes in the form and seconds on the wire.
const MINUTE_KEYS: (keyof ISettings)[] = ['flashlightTimeout', 'screenTimeout', 'inactivityTimeout'];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const changedSettings = (draft: IDraft, saved: IDraft) => SETTING_KEYS.filter((key) => !same(draft[key], saved[key]));

const toExtraUpdates = (draft: IDraft, saved: IDraft): IUpdateConfigItem[] =>
  same(draft.alertRecipients, saved.alertRecipients) ? [] : [{ key: 'ALERT_RECIPIENTS', value: draft.alertRecipients }];

const ALERT_ROWS = ['fall', 'inactivity', 'lowBattery', 'geofence', 'noShow'];

const Config = () => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = useAppSelector(selectPermission);
  // the app has no finer gate than being allowed into Senior Settings (permission seniorPhoneSetup)
  const canUpdate = canViewModule(user, permission, 'seniorPhoneSetup');

  const [saved, setSaved] = useState<IDraft>();
  const [draft, setDraft] = useState<IDraft>();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profile, config] = await Promise.all([seniorApi.getProfile(), configApi.getConfig()]);
      const next = toDraft(profile.data.data, config.data.data?.config ?? {});
      setSaved(next);
      setDraft(next);
    } catch {
      setFeedback({ type: 'error', message: t('pages.setup.loadFailed') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const settingChanges = draft && saved ? changedSettings(draft, saved) : [];
  const extraUpdates = draft && saved ? toExtraUpdates(draft, saved) : [];
  const isDirty = settingChanges.length > 0 || extraUpdates.length > 0;

  const patch = (partial: Partial<IDraft>) => {
    setFeedback(undefined);
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  const handleSync = async () => {
    if (!draft || !isDirty) return;
    setSyncing(true);
    setFeedback(undefined);
    try {
      if (settingChanges.length > 0) {
        await seniorApi.updateProfile(
          toMultipart(
            Object.fromEntries(
              settingChanges.map((key) => [
                key,
                MINUTE_KEYS.includes(key) ? (draft[key] as number) * SECONDS_PER_MINUTE : draft[key],
              ])
            )
          )
        );
      }
      if (extraUpdates.length > 0) await configApi.updateConfigs(extraUpdates);
      await load();
      setFeedback({ type: 'success', message: t('pages.setup.syncSuccess') });
    } catch (error) {
      const reason = getErrorMessage(error);
      setFeedback({
        type: 'error',
        message: reason ? `${t('pages.setup.syncFailed')} ${reason}` : t('pages.setup.syncFailed'),
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !draft) return <p className='text-sm text-muted-foreground'>{t('common.loading')}</p>;
  if (!draft) return feedback ? <p className='text-sm text-destructive'>{feedback.message}</p> : null;

  const s = (key: string) => t(`pages.setup.settings.${key}`);
  const percent = (value: number) => `${Math.round(value * 100)}%`;

  const slider = (key: 'volume' | 'brightness') => (
    <label className='flex flex-col gap-1 text-sm'>
      <span>
        {s(key)}: {percent(draft[key])}
      </span>
      <input
        type='range'
        min={0}
        max={1}
        step={0.05}
        value={draft[key]}
        disabled={!canUpdate}
        onChange={(e) => patch({ [key]: Number(e.target.value) })}
      />
    </label>
  );

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-xl font-semibold text-foreground'>{s('title')}</h1>
          <p className='text-sm text-muted-foreground'>{s('subtitle')}</p>
        </div>
        {canUpdate && (
          <button
            type='button'
            onClick={handleSync}
            disabled={!isDirty || syncing}
            className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {syncing ? t('pages.setup.syncing') : t('pages.setup.sync')}
          </button>
        )}
      </div>

      {/* the header spans the page; the content below stays a readable width */}
      <div className='mx-auto flex w-full max-w-3xl flex-col gap-4'>
        {feedback && (
          <p className={`text-sm ${feedback.type === 'success' ? 'text-foreground' : 'text-destructive'}`} role='status'>
            {feedback.message}
          </p>
        )}
        {canUpdate && isDirty && <p className='text-xs text-muted-foreground'>{t('pages.setup.unsynced')}</p>}
        {!canUpdate && <p className='text-xs text-muted-foreground'>{t('pages.setup.readOnly')}</p>}

        <SettingsCard>
          {[
            { label: s('allowedApps'), path: setupRouteAbsolute.allowedApps },
            { label: s('functionPermissions'), path: setupRouteAbsolute.functionPermissions },
          ].map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className='flex items-center justify-between gap-3 text-sm text-foreground hover:underline'
            >
              <span>{link.label}</span>
              <span className='text-primary'>{s('set')} &rsaquo;</span>
            </Link>
          ))}
        </SettingsCard>

        <SettingsCard>
          <SwitchRow
            label={s('blockUnknownNumber')}
            checked={draft.blockUnknownNumber}
            disabled={!canUpdate}
            onChange={(checked) => patch({ blockUnknownNumber: checked })}
          />
          {slider('volume')}
          {slider('brightness')}
          <SegmentedBoxes
            label={s('themeMode')}
            value={draft.themeMode}
            disabled={!canUpdate}
            options={[
              { label: s('light'), value: 'LIGHT' },
              { label: s('dark'), value: 'DARK' },
            ]}
            onChange={(value) => patch({ themeMode: value })}
          />
          <SegmentedBoxes
            label={s('flashlightOn')}
            value={draft.flashlightOn}
            disabled={!canUpdate}
            options={[
              { label: t('pages.setup.settings.off'), value: false },
              { label: t('pages.setup.settings.on'), value: true },
            ]}
            onChange={(value) => patch({ flashlightOn: value })}
          />
          <NumberToggleRow
            label={s('flashlightTimeout')}
            value={draft.flashlightTimeout}
            onValue={DEFAULT.flashlightTimeoutMinutes}
            disabled={!canUpdate}
            onChange={(value) => patch({ flashlightTimeout: value })}
          />
          <NumberToggleRow
            label={s('screenTimeout')}
            value={draft.screenTimeout}
            onValue={DEFAULT.screenTimeoutMinutes}
            disabled={!canUpdate}
            onChange={(value) => patch({ screenTimeout: value })}
          />
          <NumberToggleRow
            label={s('autoAnswerDelaySeconds')}
            value={draft.autoAnswerDelaySeconds}
            onValue={DEFAULT.autoAnswerDelaySeconds}
            disabled={!canUpdate}
            onChange={(value) => patch({ autoAnswerDelaySeconds: value })}
          />
          <NumberToggleRow
            label={s('fallDetectSensitivity')}
            value={draft.fallDetectSensitivity}
            onValue={DEFAULT.fallDetectSensitivity}
            max={MAX_FALL_DETECT_SENSITIVITY_LEVEL}
            disabled={!canUpdate}
            onChange={(value) => patch({ fallDetectSensitivity: value })}
          />
          <NumberToggleRow
            label={s('inactivityTimeout')}
            value={draft.inactivityTimeout}
            onValue={DEFAULT.inactivityTimeoutMinutes}
            disabled={!canUpdate}
            onChange={(value) => patch({ inactivityTimeout: value })}
          />
          <NumberToggleRow
            label={s('thresholdLowBattery')}
            value={draft.thresholdLowBattery}
            onValue={DEFAULT.thresholdLowBattery}
            max={100}
            disabled={!canUpdate}
            onChange={(value) => patch({ thresholdLowBattery: value })}
          />
          <NumberToggleRow
            label={s('dataRetentionDays')}
            value={draft.dataRetentionDays}
            onValue={DEFAULT.dataRetentionDays}
            max={MAX_DATA_RETENTION_DAYS}
            disabled={!canUpdate}
            onChange={(value) => patch({ dataRetentionDays: value })}
          />
        </SettingsCard>

        <SettingsCard>
          <h2 className='text-sm font-semibold text-foreground'>{t('pages.setup.config.alertRecipients')}</h2>
          <div className='grid grid-cols-[1fr_auto_auto] items-center gap-x-6 gap-y-2 text-sm'>
            <span />
            <span className='text-xs text-muted-foreground'>{t('pages.setup.config.toSenior')}</span>
            <span className='text-xs text-muted-foreground'>{t('pages.setup.config.toAdmins')}</span>
            {ALERT_ROWS.map((row) => (
              <div key={row} className='contents'>
                <span>{t(`pages.setup.config.alert.${row}`)}</span>
                {(['ForSenior', 'ToAdmins'] as const).map((suffix) => {
                  const key = `${row}Alert${suffix}`;
                  return (
                    <input
                      key={key}
                      type='checkbox'
                      aria-label={`${t(`pages.setup.config.alert.${row}`)} ${suffix}`}
                      checked={draft.alertRecipients[key] ?? false}
                      disabled={!canUpdate}
                      onChange={(e) => patch({ alertRecipients: { ...draft.alertRecipients, [key]: e.target.checked } })}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </SettingsCard>
      </div>
    </div>
  );
};

export default Config;
